import type { TypographyVariantsOptions } from "@mui/material/styles";
import type { CSSProperties } from "react";

export const SB_FONTS: Record<string, string> = {
  heading: ["Lexend", "Arial", "sans-serif"].join(","),
  body: ["Hanken Grotesk", "Arial", "sans-serif"].join(","),
};

const FONT_WEIGHTS: Record<string, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// --- Body typography ---

const DESIGN_TOKENS_BODY = [
  // body regular
  "body.large.regular",
  "body.medium.regular",
  "body.small.regular",
  "body.xSmall.regular",
  "body.overline.regular",
  // body semibold
  "body.large.semibold",
  "body.medium.semibold",
  "body.small.semibold",
  "body.xSmall.semibold",
  "body.overline.semibold",
  // body bold
  "body.large.bold",
  "body.medium.bold",
  "body.small.bold",
  "body.xSmall.bold",
  "body.overline.bold",
  // body links
  "body.large.link",
  "body.medium.link",
  "body.small.link",
  "body.xSmall.link",
  "body.overline.link",
] as const;

type DesignTokenBody = (typeof DESIGN_TOKENS_BODY)[number];

const BASE_BODY_TYPOGRAPHY: CSSProperties = {
  fontWeight: "normal",
  fontFamily: SB_FONTS["body"],
  letterSpacing: 0,
};

const BODY_FONT_SIZES = {
  large: "20px",
  medium: "16px",
  small: "14px",
  xSmall: "12px",
  overline: "12px",
};

const BODY_LINE_HEIGHTS = {
  large: "30px",
  medium: "24px",
  small: "21px",
  xSmall: "18px",
  overline: "18px",
};

const BODY_FONT_WEIGHT = {
  regular: FONT_WEIGHTS["regular"],
  semibold: FONT_WEIGHTS["semibold"],
  bold: FONT_WEIGHTS["bold"],
  link: FONT_WEIGHTS["regular"],
};

type BodySize = keyof typeof BODY_FONT_SIZES;
type BodyWeight = keyof typeof BODY_FONT_WEIGHT;

const createTypographyBodyVariant = (designToken: DesignTokenBody): CSSProperties => {
  const [, size, weight] = designToken.split(".") as ["body", BodySize, BodyWeight];

  return {
    ...BASE_BODY_TYPOGRAPHY,
    fontSize: BODY_FONT_SIZES[size],
    lineHeight: BODY_LINE_HEIGHTS[size],
    fontWeight: BODY_FONT_WEIGHT[weight],
    ...(size === "overline" ? { textTransform: "uppercase" } : {}),
    ...(weight === "link" ? { textDecoration: "underline" } : {}),
  };
};

type BodyTypography = { [key in DesignTokenBody]: CSSProperties };

const bodyTypography: BodyTypography = DESIGN_TOKENS_BODY.reduce(
  (acc, token) => ({
    ...acc,
    [token]: createTypographyBodyVariant(token),
  }),
  {} as BodyTypography
);

// --- Button typography ---

const DESIGN_TOKENS_BUTTON = [
  "button.xLarge",
  "button.large",
  "button.medium",
  "button.small",
  "button.xSmall",
] as const;

type DesignTokenButton = (typeof DESIGN_TOKENS_BUTTON)[number];

const BASE_BUTTON_TYPOGRAPHY: CSSProperties = {
  fontWeight: FONT_WEIGHTS["semibold"],
  fontFamily: SB_FONTS["body"],
  letterSpacing: 0,
};

const BUTTON_FONT_SIZE = {
  xLarge: "30px",
  large: "20px",
  medium: "16px",
  small: "14px",
  xSmall: "12px",
};

const BUTTON_LINE_HEIGHT = {
  xLarge: "30px",
  large: "25px",
  medium: "20px",
  small: "18px",
  xSmall: "15px",
};

type ButtonSize = keyof typeof BUTTON_FONT_SIZE;

const createTypographyButtonVariant = (designToken: DesignTokenButton): CSSProperties => {
  const [, size] = designToken.split(".") as ["button", ButtonSize];

  return {
    ...BASE_BUTTON_TYPOGRAPHY,
    fontSize: BUTTON_FONT_SIZE[size],
    lineHeight: BUTTON_LINE_HEIGHT[size],
  };
};

type ButtonTypography = { [key in DesignTokenButton]: CSSProperties };

const buttonTypography: ButtonTypography = DESIGN_TOKENS_BUTTON.reduce(
  (acc, token) => ({
    ...acc,
    [token]: createTypographyButtonVariant(token),
  }),
  {} as ButtonTypography
);

// --- Header Typography ---

const BASE_HEADER_TYPOGRAPHY: CSSProperties = {
  fontWeight: FONT_WEIGHTS["medium"],
  letterSpacing: 0,
  fontFamily: SB_FONTS["heading"],
};

const DESIGN_TOKENS_HEADER = [
  "heading.one",
  "heading.two",
  "heading.three",
  "heading.four",
  "heading.five",
  "heading.six",
  "heading.seven",
] as const;

type DesignTokenHeader = (typeof DESIGN_TOKENS_HEADER)[number];

export const HEADER_FONT_SIZE = {
  one: "60px",
  two: "48px",
  three: "40px",
  four: "32px",
  five: "24px",
  six: "20px",
  seven: "16px",
};

const HEADER_LINE_HEIGHT = {
  one: "68px",
  two: "56px",
  three: "48px",
  four: "40px",
  five: "32px",
  six: "24px",
  seven: "24px",
};

type HeaderSize = keyof typeof HEADER_FONT_SIZE;

const createTypographyHeaderVariant = (designToken: DesignTokenHeader): CSSProperties => {
  const [, size] = designToken.split(".") as ["heading", HeaderSize];

  return {
    ...BASE_HEADER_TYPOGRAPHY,
    fontSize: HEADER_FONT_SIZE[size],
    lineHeight: HEADER_LINE_HEIGHT[size],
  };
};

type HeaderTypography = { [key in DesignTokenHeader]: CSSProperties };

const headerTypography: HeaderTypography = DESIGN_TOKENS_HEADER.reduce(
  (acc, token) => ({
    ...acc,
    [token]: createTypographyHeaderVariant(token),
  }),
  {} as HeaderTypography
);

const heading_tag: CSSProperties = {
  fontFamily: SB_FONTS["heading"],
  fontWeight: FONT_WEIGHTS["medium"],
  fontSize: "12px",
  lineHeight: "20px",
  textTransform: "uppercase",
  letterSpacing: "0.075rem",
};

// --- General ---

const typography: TypographyVariantsOptions = {
  fontFamily: SB_FONTS["body"],
  fontWeightRegular: 400,
  fontSize: 16,
  body1: {
    fontFamily: SB_FONTS["body"],
    fontSize: "1rem",
  },
  ...headerTypography,
  ...bodyTypography,
  ...buttonTypography,
  "heading.tag": heading_tag,
};

interface OtherProps {
  "heading.tag": CSSProperties;
}

declare module "@mui/material/styles" {
  interface TypographyVariants extends BodyTypography, ButtonTypography, HeaderTypography, OtherProps {}

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions
    extends Partial<BodyTypography>, Partial<ButtonTypography>, Partial<HeaderTypography>, Partial<OtherProps> {}
}

type BodyTypographyTrue = { [K in keyof BodyTypography]: true };
type ButtonTypographyTrue = { [K in keyof ButtonTypography]: true };
type HeaderTypographyTrue = { [K in keyof HeaderTypography]: true };
type OtherPropsTrue = { [K in keyof OtherProps]: true };

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides
    extends BodyTypographyTrue, HeaderTypographyTrue, ButtonTypographyTrue, OtherPropsTrue {}
}

export default typography;
